import React from 'react'
import { stateHeadlines } from '../../i18n/stateHeadlines'
import { BodySm, Button, SecondaryButton } from './Primitives'
import * as Sentry from '@sentry/react'
import RecoveryShell from './RecoveryShell'
import { useInterfaceLanguage } from '../../i18n/useInterfaceLanguage'
import { systemCopy } from '../../i18n/systemCopy'
import { isChunkLoadError, recoverStaleChunk } from '../../lib/chunkRecovery'
import { useI18n } from '../../i18n/I18nContext'
import { useTheme } from '../../contexts/ThemeContext'

interface Props {
    children: React.ReactNode
    fallback?: React.ReactNode
}

interface State {
    hasError: boolean
    error: Error | null
}

export class ErrorBoundary extends React.Component<Props, State> {
    state: State = { hasError: false, error: null }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error }
    }

    componentDidCatch(error: Error) {
        if (isChunkLoadError(error) && recoverStaleChunk()) return
        Sentry.captureException(error)
    }

    handleReset = () => {
        this.setState({ hasError: false, error: null })
    }

    render() {
        if (!this.state.hasError) return this.props.children
        if (this.props.fallback) return this.props.fallback
        return <GlobalErrorFallback />
    }
}

// ── Global fallback (full-page crash — may render outside I18nProvider) ───────
// The small fallback dictionary works even when the providers failed to mount.

const GlobalErrorFallback: React.FC = () => {
    const language = useInterfaceLanguage()
    const copy = systemCopy[language]
    return <RecoveryShell title={copy.crashTitle} headline={stateHeadlines(language).error}>
        <Button onClick={() => window.location.reload()}>{copy.reload}</Button>
    </RecoveryShell>
}

// ── Inline fallback (lesson / overview crash — always inside provider tree) ───

export const InlineErrorFallback: React.FC<{ onReset: () => void }> = ({ onReset }) => {
    const { t } = useI18n()
    const { isDark } = useTheme()
    return (
        <div className="flex flex-col items-center justify-center gap-6 min-h-[60vh] px-6 text-center">
            <div className="max-w-xs w-full">
                <BodySm className="text-gray-900 dark:text-white font-semibold mb-2">
                    {t('errorPage.inlineMessage')}
                </BodySm>
                <div className="flex gap-3 justify-center mt-6">
                    <SecondaryButton
                        variant={isDark ? 'secondaryDark' : 'secondary'}
                        size="sm"
                        onClick={() => window.history.back()}
                    >
                        {t('errorPage.goBack')}
                    </SecondaryButton>
                    <Button
                        variant={isDark ? 'primaryDark' : 'primary'}
                        size="sm"
                        onClick={onReset}
                    >
                        {t('errorPage.tryAgain')}
                    </Button>
                </div>
            </div>
        </div>
    )
}
